"use client";

import { copyFor, examTimeframeLabel, trackLabel } from "@/lib/onboarding/copy";
import { CONCERN_OPTIONS } from "@/lib/onboarding/constants";

import type { StepProps } from "../types";
import { ContinueButton, StepFooter, StepHeader } from "../ui";

/** Story beat: reads the user's answers back, then reframes the worry as a
 *  reps gap. Every row is optional (a resumed draft can lack any answer);
 *  with no rows the card is omitted, and with no track the body is too. */
export function ReframeStep({ state, advance }: StepProps) {
  const { track, exam_timeframe, concerns } = state.answers;

  const concernLabels = (concerns ?? [])
    .map((c) => CONCERN_OPTIONS.find((o) => o.value === c)?.label)
    .filter((label): label is (typeof CONCERN_OPTIONS)[number]["label"] =>
      Boolean(label),
    );

  const rows: ReadonlyArray<{ label: string; value: string }> = [
    ...(track ? [{ label: "Training for", value: trackLabel(track) }] : []),
    ...(exam_timeframe
      ? [{ label: "Exam", value: examTimeframeLabel(exam_timeframe) }]
      : []),
    ...(concernLabels.length > 0
      ? [{ label: "Worried about", value: concernLabels.join(", ") }]
      : []),
  ];

  return (
    <>
      <StepHeader
        kicker="Here's the thing"
        title="That's not a knowledge gap. It's a reps gap."
      />
      {rows.length > 0 ? (
        <dl className="mt-7 divide-y divide-border rounded-2xl border border-border bg-card p-5">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex items-baseline justify-between gap-4 py-3 first:pt-0 last:pb-0"
            >
              <dt className="shrink-0 text-[11px] font-semibold tracking-[0.1em] text-muted-foreground uppercase">
                {row.label}
              </dt>
              <dd className="text-right text-[15px] font-medium text-foreground">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {track ? (
        <p className="mt-6 text-base leading-relaxed text-muted-foreground">
          {copyFor(track).reframeBody}
        </p>
      ) : null}
      <StepFooter>
        <ContinueButton onClick={() => advance()}>
          So how do I get reps?
        </ContinueButton>
      </StepFooter>
    </>
  );
}
